import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/axios';

const HRMS = () => {
    const [admins, setAdmins] = useState([]);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [feedback, setFeedback] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const fetchAdmins = useCallback(async () => {
        try {
            const response = await api.get('/hrms/admins');
            setAdmins(response.data);
        } catch (error) {
            console.error("Failed to fetch admins", error);
        }
    }, []);

    useEffect(() => {
        fetchAdmins();
    }, [fetchAdmins]);

    const handleCreateAdmin = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setFeedback('');
        // client-side validation
        const validate = () => {
            if (!name || name.trim().length < 2) return 'Full name is required (min 2 chars)';
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!email || !emailRegex.test(email)) return 'Please enter a valid email address';
            return null;
        };
        const validationError = validate();
        if (validationError) {
            setFeedback(validationError);
            setIsLoading(false);
            return;
        }
        try {
            const response = await api.post('/hrms/admin/create', { name, email });
            setFeedback(`Success! Admin created. Temporary Password: ${response.data.temporary_password}`);
            setName('');
            setEmail('');
            fetchAdmins(); // Refresh the list
        } catch (error) {
            setFeedback(error.response?.data?.message || 'Failed to create admin.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="p-6">
            <h1 className="text-3xl font-bold mb-6">HRMS - Manage Admins (Virtual Sevaks)</h1>

            {/* Form to create a new admin */}
            <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                <h2 className="text-xl font-semibold mb-4">Hire New Sevak</h2>
                <form onSubmit={handleCreateAdmin} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Full Name</label>
                            <input type="text" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Email Address</label>
                            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
                        </div>
                    </div>
                    <div className="flex justify-end">
                        <button type="submit" disabled={isLoading} className="px-4 py-2 bg-orange-600 text-white font-semibold rounded-md hover:bg-orange-700 disabled:bg-gray-400">
                            {isLoading ? 'Creating...' : 'Create Admin Account'}
                        </button>
                    </div>
                </form>
                {feedback && <p className="mt-4 text-sm text-gray-600 bg-gray-100 p-3 rounded">{feedback}</p>}
            </div>

            {/* Table of existing admins */}
            <div className="bg-white shadow-md rounded-lg overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {admins.map(admin => (
                            <tr key={admin.id}>
                                <td className="px-6 py-4 whitespace-nowrap">{admin.name}</td>
                                <td className="px-6 py-4 whitespace-nowrap">{admin.email}</td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                        {admin.role}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default HRMS;